import { GetProgrammaticCampaignAccountsLiftRO } from "@intentsify/dto";
import { useQuery } from "@tanstack/react-query";
import { Card, MIN_MODAL_CONTENT_HEIGHT, Timeseries } from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { ChartExport, useExportableChart } from "shared/components";
import { TimeseriesItem, TimeseriesRange } from "types";
import { DisplayPerformanceFilters } from "../../../../types";
import { MdAnalytics } from "react-icons/md";
import { isPopulatedArray } from "@intentsify/utils";

type OvertimeTimeseriesItem = TimeseriesItem<"label", { value: number }>;

type AccountsReachedOverTimeProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getAccountLifts: (
    id: string,
    filters: DisplayPerformanceFilters,
  ) => Promise<GetProgrammaticCampaignAccountsLiftRO>;
};

const TITLE = "Accounts Reached Over Time";

const AccountsReachedOverTime = (props: AccountsReachedOverTimeProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <AccountsReachedOverTimeContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const AccountsReachedOverTimeContent = ({
  campaignId,
  filters,
  getAccountLifts,
}: AccountsReachedOverTimeProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: dataLifts, isFetching: isLoadingLifts } = useQuery(
    ["getAccountLifts", campaignId, filters],
    () => getAccountLifts(campaignId, filters),
  );

  const dates = useMemo(() => {
    return {
      start: DateTime.fromObject({
        weekNumber: filters.weekFrom,
        weekYear: filters.yearFrom,
      }).toISO(),
      end: DateTime.fromObject({
        weekNumber: filters.weekTo,
        weekYear: filters.yearTo,
      }).toISO(),
    };
  }, [filters]);

  const tsData: {
    data: OvertimeTimeseriesItem[];
    timeseries: TimeseriesRange[];
  } = useMemo(() => {
    const reach: OvertimeTimeseriesItem = {
      label: "Accounts Reached",
      timeseries: [],
    };

    const timeseries: TimeseriesRange[] = [];

    if (dataLifts?.accountsReachedOverTime) {
      dataLifts?.accountsReachedOverTime.forEach((i) => {
        reach.timeseries.push({
          isoDate: i.date,
          data: { value: i.accountsReached },
        });

        timeseries.push({
          start: i.date,
          end: DateTime.fromISO(i.date).endOf("week").toISO(),
        });
      });
    }

    return {
      data: [reach],
      timeseries,
    };
  }, [dataLifts?.accountsReachedOverTime]);

  const reachedOverTimeChart = useExportableChart({
    title: TITLE,
    campaignId: Number(campaignId),
    dates,
    chart: (
      <Timeseries
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        stretch
        isLoading={isLoadingLifts}
        data={tsData.data}
        dataMainKey="label"
        timeseries={tsData.timeseries}
        variant="bars"
        pallete={["green"]}
        disableLegend
        dataKeyLabels={{
          value: "Accounts Reached",
        }}
      />
    ),
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      isLoading={isLoadingLifts}
      title={reachedOverTimeChart.title}
      isExpandable
      hasData={
        !isLoadingLifts && isPopulatedArray(dataLifts?.accountsReachedOverTime)
      }
      noDataMessage="Currently there is no data available for the selected filters or visuals."
      noDataIcon={MdAnalytics}
      actions={
        <ChartExport
          size="small"
          dates={dates}
          campaignId={Number(campaignId)}
          title={reachedOverTimeChart.title}
          onExportPNG={reachedOverTimeChart.downloadAsPng}
          data={dataLifts?.accountsReachedOverTime ?? []}
        />
      }
    >
      {reachedOverTimeChart.component}
      {reachedOverTimeChart.exportComponent}
    </Card>
  );
};

export { AccountsReachedOverTime };
